import {useCallback, useEffect, useRef, useState} from 'react'
import {useDispatch, useStore} from 'react-redux'
import {createResource, readEndpoint, updateResource} from 'redux-json-api'

import {defaultLifeStoryTitle, removeStory, storyHasContent, upsertStory} from '../lifeStoryTools'

export const SAVE_STATUS = {
  IDLE: 'idle',
  SAVING: 'saving',
  SAVED: 'saved',
  ERROR: 'error',
  UNAUTHORIZED: 'unauthorized'
}

// A save that fails on the network (or a 5xx) is retried on this schedule
// before the indicator admits defeat (TK-3179 AC 3.2). Auth and validation
// failures aren't retried: repeating them can't succeed.
export const RETRY_DELAYS_MS = [2000, 4000, 8000]

// Browsers cap the bytes a keepalive request may have in flight at unload
// (64 KiB across all such requests); a draft past this is left to the
// visibility-change flush that fires just before pagehide.
export const KEEPALIVE_BODY_LIMIT_BYTES = 60 * 1024

const retryable = error => !error.response || error.response.status >= 500

// axios rejects an aborted request with CanceledError (ERR_CANCELED).
const isAbortError = error => Boolean(error) && (error.code === 'ERR_CANCELED' || error.name === 'CanceledError' || error.name === 'AbortError')

const statusForError = error => (
  error.response && error.response.status === 401 ? SAVE_STATUS.UNAUTHORIZED : SAVE_STATUS.ERROR
)

// Writes wrap the payload as {value} (the API's Encryptable jsonb contract,
// same as jicl-configs); reads return it unwrapped, so `attributes.data` on
// fetched records is the payload itself.
const wrapPayload = payload => ({data: {value: payload}})

// The keepalive budget is in bytes on the wire, and String#length counts
// UTF-16 code units — a 25,000-character CJK answer is ~75 KB of UTF-8.
const byteLength = text => (
  typeof TextEncoder === 'function' ? new TextEncoder().encode(text).length : text.length
)

const JSON_API_HEADERS = {
  Accept: 'application/vnd.api+json',
  'Content-Type': 'application/vnd.api+json'
}

/**
 * Manages the draft state of one guided Life Story session.
 *
 * The EthicalWill record is created lazily: nothing is persisted until the
 * user provides a first meaningful answer (text or recording). Subsequent
 * changes are saved against the same record, and `publish` saves the story
 * to the vault (PDF + recordings) via the publish endpoint.
 *
 * Saves are coalesced (TK-3179): one request is in flight at a time, and
 * changes that arrive meanwhile fold into a single follow-up save of the
 * latest state — a typing burst never queues a request per keystroke. Every
 * save carries the whole payload, so the follow-up supersedes anything the
 * in-flight request missed. `saveStatus` reports what the server has actually
 * confirmed, never what the client hopes.
 *
 * Page unload is handled here too, since this is the only place that knows
 * what's in flight: whatever the origin of a pending save (autosave timer,
 * Next, a finished recording), pagehide abandons it and re-sends the latest
 * payload as a keepalive request the browser completes on its own.
 */
const useLifeStoryDraft = ({ethicalWill, firstName}) => {
  const dispatch = useDispatch()
  const store = useStore()

  const [ethicalWillId, setEthicalWillId] = useState(ethicalWill ? ethicalWill.get('id') : null)
  const [title, setTitle] = useState(
    (ethicalWill && ethicalWill.getIn(['data', 'title'])) || defaultLifeStoryTitle(firstName)
  )
  const [stories, setStories] = useState(() => {
    const savedStories = ethicalWill && ethicalWill.getIn(['data', 'stories'])

    return savedStories ? savedStories.toJS() : []
  })
  const [publishing, setPublishing] = useState(false)
  const [saveStatus, setSaveStatus] = useState(SAVE_STATUS.IDLE)
  const [lastSavedAt, setLastSavedAt] = useState(null)

  const ethicalWillIdRef = useRef(ethicalWillId)
  // Source of truth for in-flight updates: two saveStory calls in the same
  // commit (e.g. a recording completing right as the user advances) would
  // otherwise both start from the stale `stories` state and drop one answer.
  const storiesRef = useRef(stories)
  const titleRef = useRef(title)

  // The save loop currently running (request, retries, and any follow-up
  // save it owes), or null when idle.
  const inFlightRef = useRef(null)
  // Set when a save is requested while the loop is running; the loop sends
  // one more request with the latest state before it finishes.
  const dirtyRef = useRef(false)
  // Aborts the request currently on the wire (see flushForUnload).
  const abortControllerRef = useRef(null)
  // The payload the server last confirmed, serialized: a flush that changes
  // nothing (blur without typing) doesn't need a request.
  const lastConfirmedRef = useRef(
    ethicalWill ? JSON.stringify({title: titleRef.current, stories: storiesRef.current}) : null
  )
  // The keepalive request on the wire, if any: {serialized, controller,
  // request}. While it's out, the two pagehide paths (the prompt's
  // unscheduled flush and this hook's own listener) don't send the same
  // bytes twice, a newer unload payload aborts it rather than racing it, and
  // normal sends wait behind it so it can't land after newer state. Cleared
  // once it settles.
  const pendingKeepaliveRef = useRef(null)
  const saveStatusRef = useRef(saveStatus)
  const retryTimerRef = useRef(null)
  const mountedRef = useRef(true)

  saveStatusRef.current = saveStatus

  useEffect(() => {
    mountedRef.current = true

    return () => {
      mountedRef.current = false
      clearTimeout(retryTimerRef.current)
    }
  }, [])

  const updateStatus = useCallback(status => {
    if (!mountedRef.current) return

    saveStatusRef.current = status
    setSaveStatus(status)
  }, [])

  const confirm = useCallback(serialized => {
    lastConfirmedRef.current = serialized

    if (mountedRef.current) {
      setLastSavedAt(Date.now())
      updateStatus(SAVE_STATUS.SAVED)
    }
  }, [updateStatus])

  const currentPayload = () => ({title: titleRef.current, stories: storiesRef.current})

  // Set once a POST has left the browser, however it ended. A POST that
  // failed without a response may still have committed, so the next create
  // attempt first asks the server whether the record already exists.
  const createAttemptedRef = useRef(false)

  const adoptEthicalWillId = useCallback(id => {
    ethicalWillIdRef.current = id
    if (mountedRef.current) setEthicalWillId(id)
  }, [])

  // Each request gets its own AbortController so the unload path can cut it
  // off before re-sending the latest payload.
  const requestConfig = () => {
    if (typeof AbortController !== 'function') return {}

    abortControllerRef.current = new AbortController()

    return {signal: abortControllerRef.current.signal}
  }

  const send = useCallback(payload => {
    // A keepalive request is still out (the page survived pagehide). Every
    // request replaces the whole payload, so sending now could let the older
    // keepalive land last; and for a create, whether the record exists isn't
    // knowable until it settles — asking the index now could see nothing and
    // POST again. Wait, then send unless the keepalive confirmed this very
    // payload meanwhile.
    if (pendingKeepaliveRef.current) {
      return pendingKeepaliveRef.current.request.then(() => (
        JSON.stringify(payload) === lastConfirmedRef.current ? null : send(payload)
      ))
    }

    const attributes = wrapPayload(payload)

    const update = () => dispatch(updateResource({
      type: 'ethical-wills',
      id: ethicalWillIdRef.current,
      attributes
    }, requestConfig()))

    if (ethicalWillIdRef.current) return update()

    const create = () => {
      createAttemptedRef.current = true

      return dispatch(createResource({type: 'ethical-wills', attributes}, requestConfig()))
        .then(response => {
          adoptEthicalWillId(response.data.id)

          return response
        })
    }

    if (!createAttemptedRef.current) return create()

    // Retrying a create blindly could duplicate the record (or trip the
    // one-per-everplan uniqueness and never learn the id). The index is
    // scoped to the user's own everplan, so an existing record here is ours:
    // adopt it and save as an update — the full payload supersedes whatever
    // the lost POST stored.
    return dispatch(readEndpoint('ethical-wills'))
      .then(response => {
        const existing = (response.body.data || []).find(record => record.type === 'ethical-wills')

        if (!existing) return create()

        adoptEthicalWillId(existing.id)

        return update()
      })
  }, [dispatch, adoptEthicalWillId])

  const sendWithRetry = useCallback((payload, attempt = 0) => (
    send(payload)
      .catch(error => {
        if (isAbortError(error) || !retryable(error) || attempt >= RETRY_DELAYS_MS.length || !mountedRef.current)
          throw error

        return new Promise((resolve, reject) => {
          retryTimerRef.current = setTimeout(() => {
            sendWithRetry(payload, attempt + 1).then(resolve, reject)
          }, RETRY_DELAYS_MS[attempt])
        })
      })
      .finally(() => {
        abortControllerRef.current = null
      })
  ), [send])

  /**
   * Saves the current draft, coalescing with any save already in flight.
   * Resolves once the server holds the latest state (including follow-ups);
   * rejects with the final error once retries are exhausted.
   */
  const persist = useCallback(() => {
    dirtyRef.current = true

    if (inFlightRef.current) return inFlightRef.current

    const saveOnce = () => {
      dirtyRef.current = false

      const payload = currentPayload()
      const serialized = JSON.stringify(payload)

      if (serialized === lastConfirmedRef.current) {
        // Nothing to send — and if the last attempt failed, the writer has
        // since put things back the way the server has them, so the failure
        // no longer describes anything. (A lost session stays lost.)
        if (saveStatusRef.current === SAVE_STATUS.ERROR) updateStatus(SAVE_STATUS.SAVED)

        return Promise.resolve(null)
      }

      updateStatus(SAVE_STATUS.SAVING)

      return sendWithRetry(payload).then(response => {
        confirm(serialized)

        return response
      })
    }

    const loop = () => saveOnce()
      .catch(error => {
        // A request abandoned for the unload keepalive isn't a failure: if
        // the page is still alive the loop just carries on with the latest
        // payload (flushForUnload leaves it dirty), and if it isn't, nothing
        // here matters.
        if (!isAbortError(error)) throw error

        return null
      })
      .then(response => (dirtyRef.current ? loop() : response))

    inFlightRef.current = loop()
      .catch(error => {
        updateStatus(statusForError(error))
        throw error
      })
      .finally(() => {
        inFlightRef.current = null
      })

    return inFlightRef.current
  }, [sendWithRetry, updateStatus, confirm])

  /** Re-attempts a save that ran out of retries (the indicator's Retry link). */
  const retrySave = useCallback(() => persist().catch(() => {}), [persist])

  // Connectivity coming back is the moment a failed save is most likely to
  // succeed, so try once without waiting for the user to notice.
  useEffect(() => {
    const handleOnline = () => {
      if (saveStatusRef.current === SAVE_STATUS.ERROR) retrySave()
    }

    window.addEventListener('online', handleOnline)

    return () => window.removeEventListener('online', handleOnline)
  }, [retrySave])

  /**
   * The unload write. Normal requests can be cancelled with the document, so
   * the latest payload goes out as a keepalive fetch the browser finishes on
   * its own. Ordering guard: any request still on the wire is aborted first,
   * so an older payload can't land after this one and win; the loop is left
   * dirty so that a page that survives pagehide (mobile app switch, bfcache)
   * re-syncs through the normal path.
   *
   * A brand-new story is created here too (POST): the response may never be
   * read, but the record is there for the next visit to load. Only one
   * keepalive is ever outstanding: a later unload with newer words aborts it
   * first (so at most one create is out, and an older payload can't commit
   * after a newer one), and normal sends wait behind it (see `send`).
   *
   * If the page does survive and the keepalive fails, the work isn't
   * confirmed and nothing else would notice — the prompt's autosave already
   * considers it handed off — so it goes back through the normal loop for
   * retries and the indicator.
   *
   * `force` sends even when nothing is in flight (the prompt screen flushing
   * an unscheduled change). Skipped — leaving the in-flight request to take
   * its chances — when keepalive isn't available or the draft exceeds the
   * byte budget.
   */
  const flushForUnload = useCallback(({force = false} = {}) => {
    if (!force && !inFlightRef.current) return false
    if (typeof fetch !== 'function') return false

    const state = store.getState()
    const axiosConfig = state.api && state.api.endpoint && state.api.endpoint.axiosConfig

    if (!axiosConfig || !axiosConfig.baseURL) return false

    const payload = currentPayload()
    const serialized = JSON.stringify(payload)
    const pending = pendingKeepaliveRef.current

    if (serialized === lastConfirmedRef.current || (pending && pending.serialized === serialized)) return false

    const id = ethicalWillIdRef.current
    const body = JSON.stringify({
      data: {
        type: 'ethical-wills',
        ...id ? {id} : {},
        attributes: wrapPayload(payload)
      }
    })

    if (byteLength(body) > KEEPALIVE_BODY_LIMIT_BYTES) return false

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      dirtyRef.current = true
    }

    if (pending && pending.controller) pending.controller.abort()

    if (!id) createAttemptedRef.current = true

    const controller = typeof AbortController === 'function' ? new AbortController() : null
    let failed = false

    const request = fetch(`${axiosConfig.baseURL}/ethical-wills${id ? `/${id}` : ''}`, {
      method: id ? 'PATCH' : 'POST',
      keepalive: true,
      headers: {...axiosConfig.headers, ...JSON_API_HEADERS},
      body,
      ...controller ? {signal: controller.signal} : {}
    })
      .then(response => {
        if (!response.ok) {
          failed = true

          return null
        }

        // The page may survive pagehide; a response that does make it back
        // is a confirmation like any other, and a create's id lets later
        // saves update.
        confirm(serialized)

        if (id) return null

        return response.json().then(json => {
          if (json && json.data && json.data.id && !ethicalWillIdRef.current) adoptEthicalWillId(json.data.id)
        })
      })
      .catch(error => {
        // Superseded by a newer unload payload isn't a failure.
        failed = !isAbortError(error)
      })
      .finally(() => {
        if (pendingKeepaliveRef.current && pendingKeepaliveRef.current.request === request)
          pendingKeepaliveRef.current = null

        // Still here and the server doesn't have these words: the normal loop
        // retries, and reports it if that fails too. (A skip if the writer
        // has since moved on and that state is confirmed.)
        if (failed && mountedRef.current) persist().catch(() => {})
      })

    pendingKeepaliveRef.current = {serialized, controller, request}

    return true
  }, [store, adoptEthicalWillId, confirm, persist])

  useEffect(() => {
    const handlePageHide = () => flushForUnload()

    window.addEventListener('pagehide', handlePageHide)

    return () => window.removeEventListener('pagehide', handlePageHide)
  }, [flushForUnload])

  /** Adds or replaces one story and persists the draft. No-ops until there is something worth saving. */
  const saveStory = useCallback((story, {keepalive = false} = {}) => {
    const nextStories = upsertStory({stories: storiesRef.current, story})

    storiesRef.current = nextStories
    setStories(nextStories)

    const hasMeaningfulAnswer = nextStories.some(nextStory => !nextStory.skipped && storyHasContent(nextStory))

    if (!ethicalWillIdRef.current && !hasMeaningfulAnswer)
      return Promise.resolve()

    if (keepalive) {
      flushForUnload({force: true})

      return Promise.resolve()
    }

    return persist().catch(() => {})
  }, [persist, flushForUnload])

  const removeStoryForPrompt = useCallback(promptId => {
    const nextStories = removeStory({stories: storiesRef.current, promptId})

    storiesRef.current = nextStories
    setStories(nextStories)

    if (!ethicalWillIdRef.current)
      return Promise.resolve()

    return persist().catch(() => {})
  }, [persist])

  const saveTitle = useCallback(nextTitle => {
    const cleanTitle = nextTitle.trim() || defaultLifeStoryTitle(firstName)

    titleRef.current = cleanTitle
    setTitle(cleanTitle)

    if (!ethicalWillIdRef.current)
      return Promise.resolve()

    return persist().catch(() => {})
  }, [persist, firstName])

  /** Saves the story to the vault: builds the PDF and attaches it plus recordings to the tracked response group. */
  const publish = useCallback(() => {
    if (!ethicalWillIdRef.current)
      return Promise.resolve(null)

    setPublishing(true)

    // Wait for in-flight saves (and the follow-up they owe) so the published
    // PDF includes every answer.
    return (inFlightRef.current || Promise.resolve())
      .catch(() => {})
      .then(() => dispatch(createResource({
        type: 'ethical-will-publications',
        attributes: {'ethical-will-id': ethicalWillIdRef.current}
      })))
      .finally(() => setPublishing(false))
  }, [dispatch])

  return {
    ethicalWillId,
    title,
    stories,
    publishing,
    saveStatus,
    lastSavedAt,
    retrySave,
    saveStory,
    removeStory: removeStoryForPrompt,
    saveTitle,
    publish
  }
}

export default useLifeStoryDraft
